import Message, { SendStatus } from '@/models/message'

const MAX_SIZE_MB = 20
// Must match `MAX_SIZE = 20.megabytes` in Api::Chat::MessagesController
// exactly, i.e. binary megabytes. The shared convertToBytes() helper uses
// decimal MB (10^6), which is ~4.6% stricter and would refuse files the server
// accepts -- harmless while this check was cosmetic, but not now that it stops
// the upload.
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif']
const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'video/mp4',
]
// Mirrors Chat::Helper::SNIFFABLE_CONTENT_TYPES, plus the empty string that
// browsers report when they cannot determine a type.
const SNIFFABLE_CONTENT_TYPES = ['', 'application/octet-stream']

export class FileMessage extends Message {
  files: FileList
  messageType: 'image' | 'file'
  progress: number
  abortController: AbortController

  constructor(messageOptions: any, files: FileList, abortController: AbortController) {
    super(messageOptions)
    this.files = files
    this.progress = 1
    this.abortController = abortController
    this.messageType = this.resolveMessageType(files)
    this.validateFileSize(files)
  }

  sendSucceeded(attachments: any = null, messageType: 'image' | 'file' | null = null) {
    if (messageType && messageType !== this.messageType) {
      this.messageType = messageType
      this.updateMessageCallback(this.channelId, this.id, 'messageType', messageType)
    }
    this.updateMessageCallback(this.channelId, this.id, 'sendStatus', SendStatus.SUCCESS)
    if (this.messageType == 'file') this.updateMessageCallback(this.channelId, this.id, 'attachments', attachments)
  }

  sendFailed(errorMessage: any) {
    this.updateMessageCallback(this.channelId, this.id, 'sendStatus', SendStatus.FAIL)
    if (errorMessage) {
      this.updateMessageCallback(this.channelId, this.id, 'error', errorMessage)
    }
  }

  resolveMessageType(files) {
    const contentType = files[0].type

    if (ALLOWED_IMAGE_TYPES.includes(contentType)) return 'image'
    if (ALLOWED_FILE_TYPES.includes(contentType)) return 'file'

    // Browsers do not always report a usable type: it can be empty, or a
    // generic octet-stream. The server sniffs the real type from the bytes
    // (Chat::Helper#resolve_content_type), so defer to it instead of refusing
    // here. Refusing on a *declared* content type is the mistake behind
    // THR-174, and this list is a stale copy of the server's old allowlist.
    if (SNIFFABLE_CONTENT_TYPES.includes(contentType)) return 'file'

    this.sendStatus = SendStatus.FAIL
    this.error = `Unsupported file type`
    return 'file'
  }

  updateProgress(progress) {
    this.updateMessageCallback(this.channelId, this.id, 'progress', progress)

    if (progress == 100) {
      this.updateMessageCallback(this.channelId, this.id, 'sendStatus', SendStatus.UPLOAD_COMPLETE)
    }
  }

  cancel() {
    this.abortController.abort()
    this.updateMessageCallback(this.channelId, this.id, 'sendStatus', SendStatus.FAIL)
    this.updateMessageCallback(this.channelId, this.id, 'error', 'File upload has been cancelled.')
  }

  validateFileSize(files) {
    if (files[0].size > MAX_SIZE_BYTES) {
      this.sendStatus = SendStatus.FAIL
      this.error = `File size exceeds ${MAX_SIZE_MB}MB limit`
    }
  }
}
